import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import HeaderSlider from "./header";
import BookNow from "./bookNow";
import { FiHome } from 'react-icons/fi';
import Copyright from "./copyright";
import Footer from "./footer";
import About from "./about";
import Airport from "./airport";
import Services from "./services";
import Details from "./details";
import Contact from "./contact";
import { Typography, Grid, Card, CardContent, Button, Paper, Container, ListItemIcon } from "@mui/material";
import {  AirportShuttle, Event, Business, Phone, DirectionsCar, Info } from "@mui/icons-material";

const Home = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 900px)");
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMenu, setSelectedMenu] = useState("HOME");
  const [hoveredMenu, setHoveredMenu] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menuItems = [
    { label: "Home", path: "/", icon: <FiHome /> },
    { label: "About Watford Taxi", path: "/aboutwatfordtaxi", icon: <Info /> },
    { label: "Airport Taxi", path: "/airporttaxi", icon: <AirportShuttle /> },
    { label: "Services", path: "/services", icon: <DirectionsCar /> },
    { label: "Contact Us", path: "/contactus", icon: <Phone /> },
  ];

  const renderContent = () => {
    return (
      <>
        {/* HeaderSlider is hidden only on details page */}
        {!location.pathname.startsWith("/details") && <HeaderSlider selectedMenu={selectedMenu} />}
        
        {(() => {
          switch (true) {
            case location.pathname === "/aboutwatfordtaxi":
              return <About />;
            case location.pathname === "/airporttaxi":
              return <Airport />;
            case location.pathname === "/services":
              return <Services />;
            case location.pathname === "/contactus":
              return <Contact />;
            case location.pathname.startsWith("/details"):
              return <Details />;
            default:
              return (
                <>
                  <WelcomeSection />
                  <Box id="book-now" sx={{ py: 8 }}>
                    <BookNow />
                  </Box>
                  {/* <WhyChooseUs />
                  <Testimonials /> */}
                </>
              );
          }
        })()}
      </>
    );
  };

  const WelcomeSection = () => (
    <Box sx={{ 
      py: 8,
      px: isMobile ? 2 : 6,
      background: 'linear-gradient(135deg, #363E66 0%, #1E2347 100%)',
      color: 'white'
    }}>
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography 
              variant="h3" 
              component="h1"
              sx={{
                fontWeight: 700,
                color: "#FFC61A",
                mb: 3,
                fontSize: isMobile ? '2rem' : '3rem'
              }}
            >
              Watford's Premier Taxi Service
            </Typography>
            <Typography 
              variant="h5" 
              component="h2"
              sx={{
                fontWeight: 500,
                mb: 3,
                lineHeight: 1.3
              }}
            >
              Reliable, Affordable & Available 24/7
            </Typography>
            <Typography 
              variant="body1" 
              paragraph
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: '1.1rem'
              }}
            >
              Experience the best taxi service in Watford with our professional drivers, 
              modern fleet, and commitment to punctuality.
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 2, 
              flexWrap: 'wrap',
              width: '100%' 
            }}>
              <Button 
                variant="contained" 
                size="large"
                sx={{
                  bgcolor: "#FFC61A",
                  color: "#363E66",
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    bgcolor: "#363E66",
                    color: "#FFC61A",
                    border: '1px solid #FFC61A'
                  }
                }}
                onClick={() => document.getElementById('book-now')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Book Now
              </Button>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper elevation={6} sx={{ 
              p: 3, 
              borderRadius: 2,  
              bgcolor: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,198,26,0.3)'
            }}>
              <Typography 
                variant="h5" 
                component="h3" 
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: "#FFC61A",
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <DirectionsCar sx={{ mr: 1.5 }} /> 
                Our Services
              </Typography>
              
              <Grid container spacing={2}>
                {[
                  { icon: <AirportShuttle sx={{ color: "#FFC61A", fontSize: 40 }} />, 
                    title: "Local Rides", 
                    desc: "Quick transportation within Watford" },
                  { icon: <Business sx={{ color: "#FFC61A", fontSize: 40 }} />, 
                    title: "Airport Transfers", 
                    desc: "To all major airports" },
                  { icon: <Business sx={{ color: "#FFC61A", fontSize: 40 }} />, 
                    title: "Corporate Travel", 
                    desc: "Premium service for professionals" },
                  { icon: <Event sx={{ color: "#FFC61A", fontSize: 40 }} />, 
                    title: "Event Transport", 
                    desc: "Weddings and special occasions" }
                ].map((service, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card sx={{ 
                      height: '100%', 
                      bgcolor: 'rgba(255,255,255,0.05)',
                      borderLeft: '4px solid #FFC61A',
                      transition: 'transform 0.3s',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 20px rgba(0,0,0,0.2)'
                      }
                    }}>
                      <CardContent sx={{ p: 2 }}>
                        {service.icon}
                        <Typography variant="h6" sx={{ mt: 1, fontWeight: 600, color: 'white' }}>
                          {service.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                          {service.desc}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );

  // Removed unused sections to satisfy CI lint rules

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Main AppBar (Header) - Always visible */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: isScrolled ? "#FFC61A" : "#FFC61A",
          color: isScrolled ? "#fff" : "#000",
          boxShadow: isScrolled ? 4 : "none",
          transition: "background-color 0.3s, box-shadow 0.3s",
          zIndex: 10,
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ padding: "5px", display: "flex", alignItems: "center" }}
          onClick={() => navigate("/")}
          >
            <img
              src="/taxi2.png"
              alt="Logo"
              style={{
                width: isMobile ? "120px" : "400px",
                height: isMobile ? "50px" : "90px",
                cursor: "pointer",
              }}
            />
          </Box>

          {!isMobile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexGrow: 1,
                backgroundColor: !isScrolled ? "white" : "transparent",
                padding: "0px",
                marginLeft: "50px",
                boxShadow: !isScrolled ? "8px 8px 0px #FFC61A" : "none",
                transition: "background-color 0.3s, box-shadow 0.3s",
                border: "1px solid transparent",
                borderRadius: "3px",
              }}
            >
              <List sx={{ display: "flex", justifyContent: "space-around", width: "100%" }}>
                {menuItems.map((item) => (
                  <ListItem key={item.label} sx={{ width: "auto" }}>
                    <ListItemButton
                      onClick={() => {
                        navigate(item.path);
                        setSelectedMenu(item.label);
                      }}
                      onMouseEnter={() => setHoveredMenu(item.label)}
                      onMouseLeave={() => setHoveredMenu("")}
                      sx={{
                        color:
                          selectedMenu === item.label || hoveredMenu === item.label
                            ? "#FFC61A"
                            : "transparent",
                        position: "relative",
                        "&::after": {
                          content: hoveredMenu === item.label || selectedMenu === item.label ? '""' : "none",
                          position: "absolute",
                          bottom: "-5px",
                          left: "0",
                          width: "100%",
                          height: "3px",
                          backgroundImage: "url('./active.png')",
                          backgroundSize: "cover",
                          backgroundRepeat: "no-repeat",
                          textTransform: "uppercase"
                        },
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: selectedMenu === (item.label || hoveredMenu === item.label) && !isScrolled 
                          ? "#FFC61A" 
                          : isScrolled && (selectedMenu === item.label || hoveredMenu === item.label)
                            ? "#363E66" 
                            : "#000",
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {isMobile && (
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setMenuOpen(true)}
              sx={{
                backgroundColor: isScrolled ? "transparent" : "white",
                borderRadius: "5px",
                padding: "5px",
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer Menu */}
      <Drawer anchor="right" open={menuOpen} onClose={() => setMenuOpen(false)}>
        <List sx={{ width: "250px" }}>
          {menuItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  setSelectedMenu(item.label);
                  setMenuOpen(false);
                }}
                sx={{
                  px: 3,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                {item.icon && (
                  <ListItemIcon sx={{ minWidth: '36px', color: 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ 
                    fontSize: "16px", 
                    fontWeight: "bold",
                    color: selectedMenu === item.label ? "#FFC61A" : "inherit",
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ pt: isMobile ? 7 : 10 }}>
        {renderContent()}
      </Box>

      {/* Footer - Always visible */}
      <Footer />
      <Copyright />
    </Box>
  );
};

export default Home;